import SearchIcon from '@/assets/icons/search.svg?react';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/lib';
import { RootState, useAppDispatch } from '@/reducers';
import { PayloadAction } from '@reduxjs/toolkit';
import { ComponentProps, FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

type SearchInputProps = {
  stateSelector: (state: RootState) => string;
  stateUpdater: (searchText: string) => PayloadAction<string>;
  wrapperClassName?: string;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange'>;

export const SearchInput: FC<SearchInputProps> = ({ stateSelector, stateUpdater, wrapperClassName, className, ...inputProps }) => {
  const dispatch = useAppDispatch();

  const stateSearchText = useSelector(stateSelector);
  const [searchText, setSearchText] = useState<string>(stateSearchText || '');
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    dispatch(stateUpdater(debouncedSearchText));
  }, [debouncedSearchText]);

  return (
    <div
      className={twMerge(
        'h-11 w-80 border flex rounded-lg gap-2 items-center border-primary-border bg-primary-background px-3 py-2 shadow-xs',
        '[&:has(input:focus)]:ring-2 ring-brand-600 ring-offset-2',
        wrapperClassName
      )}
    >
      <SearchIcon className='text-quaternary-400' />
      <input
        {...inputProps}
        type='search'
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className={twMerge(
          'border-none focus:!shadow-none focus:!ring-0 p-0',
          'grow text-primary-900 placeholder:text-other-textPlaceholder ',
          'disabled:cursor-not-allowed disabled:text-primary-900/25',
          className
        )}
      />
    </div>
  );
};
